import { zodResolver } from "@hookform/resolvers/zod";
import type React from "react";
import { forwardRef, useEffect, useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import FieldError from "../components/FieldError";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import ValidationBanner from "../components/ValidationBanner";
import { useAuth } from "../context/AuthContext";
import {
	type ConfirmResetFormData,
	confirmResetSchema,
	type ForgotPasswordFormData,
	forgotPasswordSchema,
	type LoginFormData,
	loginSchema,
	type NewPasswordRequiredFormData,
	newPasswordRequiredSchema,
} from "../schemas/login";
import { NewPasswordRequiredError } from "../services/auth/authService";
import { notifications } from "../utils/notifications";

type LoginView = "signin" | "forgot" | "confirmReset";

const MG_DARK =
	"linear-gradient(247deg, #8B6914 -5%, #8B4532 48%, #1A5A6B 105%)";

/* ── Particles canvas ──────────────────────────────────────── */
const Particles = ({
	color = "255,255,255",
	opacity = 0.05,
	count = 24,
}: {
	color?: string;
	opacity?: number;
	count?: number;
}) => {
	const ref = useRef<HTMLCanvasElement>(null);
	useEffect(() => {
		const c = ref.current;
		if (!c) return;
		// Skip animation for users who prefer reduced motion
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
		const ctx = c.getContext("2d");
		if (!ctx) return;
		let animId: number;
		let w = 0;
		let h = 0;
		const resize = () => {
			const p = c.parentElement;
			if (!p) return;
			w = p.offsetWidth;
			h = p.offsetHeight;
			c.width = w * 2;
			c.height = h * 2;
			c.style.width = `${w}px`;
			c.style.height = `${h}px`;
			ctx.setTransform(2, 0, 0, 2, 0, 0);
		};
		resize();
		// nosemgrep: nodejs_scan.javascript-crypto-rule-node_insecure_random_generator -- particle animation visuals; non-cryptographic
		const pts = Array.from({ length: count }, () => ({
			x: Math.random() * w,
			y: Math.random() * h,
			vx: (Math.random() - 0.5) * 0.2,
			vy: (Math.random() - 0.5) * 0.2,
			r: Math.random() * 1.5 + 0.5,
		}));
		const draw = () => {
			ctx.clearRect(0, 0, w, h);
			for (let i = 0; i < pts.length; i++) {
				const a = pts[i];
				a.x += a.vx;
				a.y += a.vy;
				if (a.x < 0 || a.x > w) a.vx *= -1;
				if (a.y < 0 || a.y > h) a.vy *= -1;
				for (let j = i + 1; j < pts.length; j++) {
					const b = pts[j];
					const d = Math.hypot(a.x - b.x, a.y - b.y);
					if (d < 120) {
						ctx.beginPath();
						ctx.moveTo(a.x, a.y);
						ctx.lineTo(b.x, b.y);
						ctx.strokeStyle = `rgba(${color},${opacity * (1 - d / 120)})`;
						ctx.lineWidth = 0.5;
						ctx.stroke();
					}
				}
				ctx.beginPath();
				ctx.arc(a.x, a.y, a.r, 0, Math.PI * 2);
				ctx.fillStyle = `rgba(${color},${opacity * 0.8})`;
				ctx.fill();
			}
			animId = requestAnimationFrame(draw);
		};
		draw();
		window.addEventListener("resize", resize);
		return () => {
			cancelAnimationFrame(animId);
			window.removeEventListener("resize", resize);
		};
	}, [color, opacity, count]);
	return (
		<canvas
			ref={ref}
			aria-hidden="true"
			tabIndex={-1}
			className="pointer-events-none absolute inset-0"
		/>
	);
};

/* ── Concentric rings ──────────────────────────────────────── */
const Rings = ({
	ringColor = "rgba(255,255,255,0.08)",
	className = "",
}: {
	ringColor?: string;
	className?: string;
}) => (
	<div
		aria-hidden="true"
		className={`pointer-events-none absolute ${className}`}
		style={{ width: "70%", aspectRatio: "1" }}
	>
		{[0, 1, 2, 3, 4].map((i) => (
			<div
				key={i}
				className="absolute rounded-full"
				style={{ inset: `${i * 12}%`, border: `1px solid ${ringColor}` }}
			/>
		))}
	</div>
);

/* ── Password input with show/hide toggle ──────────────────── */
interface PasswordInputProps {
	id: string;
	name?: string;
	value?: string;
	onChange?: React.ChangeEventHandler<HTMLInputElement>;
	onBlur?: React.FocusEventHandler<HTMLInputElement>;
	placeholder?: string;
	autoComplete?: string;
	minLength?: number;
	ariaInvalid?: boolean;
	ariaDescribedby?: string;
	className?: string;
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
	(
		{
			id,
			name,
			value,
			onChange,
			onBlur,
			placeholder,
			autoComplete,
			minLength,
			ariaInvalid,
			ariaDescribedby,
			className,
		},
		ref,
	) => {
		const [show, setShow] = useState(false);
		return (
			<div className="relative">
				<Input
					id={id}
					ref={ref}
					name={name}
					type={show ? "text" : "password"}
					{...(value !== undefined ? { value } : {})}
					onChange={onChange}
					onBlur={onBlur}
					autoComplete={autoComplete}
					placeholder={placeholder}
					minLength={minLength}
					className={`pr-10 ${className ?? ""}`}
					aria-invalid={ariaInvalid}
					aria-describedby={ariaDescribedby}
				/>
				<button
					type="button"
					aria-label={show ? "Hide password" : "Show password"}
					className="absolute top-1/2 right-3 -translate-y-1/2 text-(--color-muted-foreground) transition-colors hover:text-(--color-foreground)"
					onClick={() => setShow((s) => !s)}
				>
					{show ? (
						<svg
							aria-hidden="true"
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
							<path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
							<line x1="1" y1="1" x2="23" y2="23" />
						</svg>
					) : (
						<svg
							aria-hidden="true"
							width="16"
							height="16"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
							<circle cx="12" cy="12" r="3" />
						</svg>
					)}
				</button>
			</div>
		);
	},
);
PasswordInput.displayName = "PasswordInput";

/* ── Shared form content (all 4 view states) ───────────────── */
interface FormContentProps {
	view: LoginView;
	setView: (v: LoginView) => void;
	onSwitchToForgot: () => void;
	emailForDisplay: string;
	error: string;
	loading: boolean;
	newPasswordRequired: boolean;
	loginForm: UseFormReturn<LoginFormData, undefined, LoginFormData>;
	forgotForm: UseFormReturn<
		ForgotPasswordFormData,
		undefined,
		ForgotPasswordFormData
	>;
	confirmResetForm: UseFormReturn<
		ConfirmResetFormData,
		undefined,
		ConfirmResetFormData
	>;
	newPasswordForm: UseFormReturn<
		NewPasswordRequiredFormData,
		undefined,
		NewPasswordRequiredFormData
	>;
	onValidSignIn: (data: LoginFormData) => void;
	onValidForgot: (data: ForgotPasswordFormData) => void;
	onValidConfirmReset: (data: ConfirmResetFormData) => void;
	onValidNewPassword: (data: NewPasswordRequiredFormData) => void;
}

const FormContent = ({
	view,
	setView,
	onSwitchToForgot,
	emailForDisplay,
	error,
	loading,
	newPasswordRequired,
	loginForm,
	forgotForm,
	confirmResetForm,
	newPasswordForm,
	onValidSignIn,
	onValidForgot,
	onValidConfirmReset,
	onValidNewPassword,
}: FormContentProps) => {
	const BackBtn = ({
		label,
		onClick,
	}: {
		label: string;
		onClick: () => void;
	}) => (
		<button
			type="button"
			className="mb-5 flex items-center gap-1.5 font-medium text-(--color-neutral-0)/80 text-xs transition-opacity hover:opacity-100"
			onClick={onClick}
		>
			<svg
				aria-hidden="true"
				width="14"
				height="14"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			>
				<polyline points="15 18 9 12 15 6" />
			</svg>
			{label}
		</button>
	);

	if (view === "forgot") {
		const { errors } = forgotForm.formState;
		return (
			<>
				<BackBtn label="Back to sign in" onClick={() => setView("signin")} />
				<h2 className="mb-1.5 font-bold text-(--color-foreground) text-2xl">
					Reset password
				</h2>
				<p className="mb-7 text-(--color-muted-foreground) text-sm opacity-70">
					Enter your email and we'll send you a reset code.
				</p>
				<form onSubmit={forgotForm.handleSubmit(onValidForgot)}>
					{error && (
						<Alert variant="destructive" className="mb-4">
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}
					{Object.keys(errors).length > 0 && (
						<div className="mb-4">
							<ValidationBanner
								message="Please fix the errors below"
								fields={Object.values(errors)
									.map((e) => (e && "message" in e ? String(e.message) : ""))
									.filter(Boolean)}
								aria-live="polite"
							/>
						</div>
					)}
					<div className="mb-5 space-y-1.5">
						<Label htmlFor="forgot-email">Email address</Label>
						<Input
							id="forgot-email"
							type="email"
							{...forgotForm.register("email")}
							autoComplete="email"
							placeholder="you@example.com"
							aria-invalid={!!errors.email}
							aria-describedby={errors.email ? "forgot-email-error" : undefined}
							className={
								errors.email ? "border-(--color-error-500)" : undefined
							}
						/>
						{errors.email && (
							<FieldError
								id="forgot-email-error"
								message={String(errors.email?.message ?? "")}
							/>
						)}
					</div>
					<Button type="submit" size="lg" disabled={loading} className="w-full">
						{loading ? "Sending…" : "Send reset code"}
					</Button>
				</form>
			</>
		);
	}

	if (view === "confirmReset") {
		const { errors } = confirmResetForm.formState;
		return (
			<>
				<BackBtn label="Back" onClick={() => setView("forgot")} />
				<h2 className="mb-1.5 font-bold text-(--color-foreground) text-2xl">
					Set new password
				</h2>
				<p className="mb-7 text-(--color-muted-foreground) text-sm opacity-70">
					Enter the code we sent to{" "}
					<span className="font-medium text-(--color-foreground)">
						{emailForDisplay}
					</span>
					.
				</p>
				<form onSubmit={confirmResetForm.handleSubmit(onValidConfirmReset)}>
					{error && (
						<Alert variant="destructive" className="mb-4">
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}
					{Object.keys(errors).length > 0 && (
						<div className="mb-4">
							<ValidationBanner
								message="Please fix the errors below"
								fields={Object.values(errors)
									.map((e) => (e && "message" in e ? String(e.message) : ""))
									.filter(Boolean)}
								aria-live="polite"
							/>
						</div>
					)}
					<div className="mb-4 space-y-1.5">
						<Label htmlFor="reset-code">Verification code</Label>
						<Input
							id="reset-code"
							type="text"
							{...confirmResetForm.register("resetCode")}
							autoComplete="one-time-code"
							placeholder="Enter code from email"
							aria-invalid={!!errors.resetCode}
							aria-describedby={
								errors.resetCode ? "reset-code-error" : undefined
							}
							className={
								errors.resetCode ? "border-(--color-error-500)" : undefined
							}
						/>
						{errors.resetCode && (
							<FieldError
								id="reset-code-error"
								message={String(errors.resetCode?.message ?? "")}
							/>
						)}
					</div>
					<div className="mb-4 space-y-1.5">
						<Label htmlFor="reset-new-password">New password</Label>
						<Controller
							control={confirmResetForm.control}
							name="newPassword"
							render={({ field }) => (
								<PasswordInput
									ref={field.ref}
									id="reset-new-password"
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									placeholder="Enter new password"
									minLength={8}
									autoComplete="new-password"
									ariaInvalid={!!errors.newPassword}
									ariaDescribedby={
										errors.newPassword ? "reset-new-password-error" : undefined
									}
									className={
										errors.newPassword
											? "border-(--color-error-500)"
											: undefined
									}
								/>
							)}
						/>
						{errors.newPassword && (
							<FieldError
								id="reset-new-password-error"
								message={String(errors.newPassword?.message ?? "")}
							/>
						)}
					</div>
					<div className="mb-6 space-y-1.5">
						<Label htmlFor="reset-confirm-password">Confirm new password</Label>
						<Controller
							control={confirmResetForm.control}
							name="confirmPassword"
							render={({ field }) => (
								<PasswordInput
									ref={field.ref}
									id="reset-confirm-password"
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									placeholder="Confirm new password"
									autoComplete="new-password"
									ariaInvalid={!!errors.confirmPassword}
									ariaDescribedby={
										errors.confirmPassword
											? "reset-confirm-password-error"
											: undefined
									}
									className={
										errors.confirmPassword
											? "border-(--color-error-500)"
											: undefined
									}
								/>
							)}
						/>
						{errors.confirmPassword && (
							<FieldError
								id="reset-confirm-password-error"
								message={String(errors.confirmPassword?.message ?? "")}
							/>
						)}
					</div>
					<Button type="submit" size="lg" disabled={loading} className="w-full">
						{loading ? "Resetting…" : "Reset password"}
					</Button>
				</form>
			</>
		);
	}

	if (newPasswordRequired) {
		const { errors } = newPasswordForm.formState;
		return (
			<>
				<h2 className="mb-1.5 font-bold text-(--color-foreground) text-2xl">
					Set new password
				</h2>
				<p className="mb-7 text-(--color-muted-foreground) text-sm opacity-70">
					Your account requires a password change on first login.
				</p>
				<form onSubmit={newPasswordForm.handleSubmit(onValidNewPassword)}>
					{error && (
						<Alert variant="destructive" className="mb-4">
							<AlertDescription>{error}</AlertDescription>
						</Alert>
					)}
					{Object.keys(errors).length > 0 && (
						<div className="mb-4">
							<ValidationBanner
								message="Please fix the errors below"
								fields={Object.values(errors)
									.map((e) => (e && "message" in e ? String(e.message) : ""))
									.filter(Boolean)}
								aria-live="polite"
							/>
						</div>
					)}
					<div className="mb-4 space-y-1.5">
						<Label htmlFor="newPassword">New password</Label>
						<PasswordInput
							id="newPassword"
							{...newPasswordForm.register("newPassword")}
							minLength={8}
							autoComplete="new-password"
							placeholder="Enter new password"
							ariaInvalid={!!errors.newPassword}
							ariaDescribedby={
								errors.newPassword ? "newPassword-error" : undefined
							}
							className={
								errors.newPassword ? "border-(--color-error-500)" : undefined
							}
						/>
						{errors.newPassword && (
							<FieldError
								id="newPassword-error"
								message={String(errors.newPassword?.message ?? "")}
							/>
						)}
						<p className="text-(--color-muted-foreground) text-xs">
							Minimum 8 characters
						</p>
					</div>
					<div className="mb-6 space-y-1.5">
						<Label htmlFor="confirmPassword">Confirm new password</Label>
						<PasswordInput
							id="confirmPassword"
							{...newPasswordForm.register("confirmPassword")}
							autoComplete="new-password"
							placeholder="Confirm new password"
							ariaInvalid={!!errors.confirmPassword}
							ariaDescribedby={
								errors.confirmPassword ? "confirmPassword-error" : undefined
							}
							className={
								errors.confirmPassword
									? "border-(--color-error-500)"
									: undefined
							}
						/>
						{errors.confirmPassword && (
							<FieldError
								id="confirmPassword-error"
								message={String(errors.confirmPassword?.message ?? "")}
							/>
						)}
					</div>
					<Button type="submit" size="lg" disabled={loading} className="w-full">
						{loading ? "Setting password…" : "Set password & sign in"}
					</Button>
				</form>
			</>
		);
	}

	/* Default: sign in */
	const { errors } = loginForm.formState;
	return (
		<>
			<h2 className="mb-1.5 font-bold text-(--color-foreground) text-2xl">
				Welcome back
			</h2>
			<p className="mb-7 text-(--color-muted-foreground) text-sm opacity-70">
				Sign in to your partner account
			</p>
			<form onSubmit={loginForm.handleSubmit(onValidSignIn)}>
				{error && (
					<Alert variant="destructive" className="mb-4">
						<AlertDescription>{error}</AlertDescription>
					</Alert>
				)}
				{Object.keys(errors).length > 0 && (
					<div className="mb-4">
						<ValidationBanner
							message="Please fix the errors below"
							fields={Object.values(errors)
								.map((e) => (e && "message" in e ? String(e.message) : ""))
								.filter(Boolean)}
							aria-live="polite"
						/>
					</div>
				)}
				<div className="mb-4 space-y-1.5">
					<Label htmlFor="email">Email address</Label>
					<Input
						id="email"
						type="email"
						{...loginForm.register("email")}
						autoComplete="email"
						placeholder="you@example.com"
						aria-invalid={!!errors.email}
						aria-describedby={errors.email ? "email-error" : undefined}
						className={errors.email ? "border-(--color-error-500)" : undefined}
					/>
					{errors.email && (
						<FieldError
							id="email-error"
							message={String(errors.email?.message ?? "")}
						/>
					)}
				</div>
				<div className="mb-6 space-y-1.5">
					<div className="flex items-center justify-between">
						<Label htmlFor="password">Password</Label>
						<button
							type="button"
							className="font-medium text-(--color-neutral-0)/80 text-xs transition-opacity hover:opacity-100"
							onClick={onSwitchToForgot}
						>
							Forgot password?
						</button>
					</div>
					<Controller
						control={loginForm.control}
						name="password"
						render={({ field }) => (
							<PasswordInput
								ref={field.ref}
								id="password"
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
								autoComplete="current-password"
								placeholder="Enter your password"
								ariaInvalid={!!errors.password}
								ariaDescribedby={errors.password ? "password-error" : undefined}
								className={
									errors.password ? "border-(--color-error-500)" : undefined
								}
							/>
						)}
					/>
					{errors.password && (
						<FieldError
							id="password-error"
							message={String(errors.password?.message ?? "")}
						/>
					)}
				</div>
				<Button type="submit" size="lg" disabled={loading} className="w-full">
					{loading ? "Signing in…" : "Sign in"}
				</Button>
				<p className="mt-6 text-center text-(--color-muted-foreground) text-sm">
					Want to become a partner?{" "}
					<a
						href="/onboard"
						className="font-medium text-(--color-neutral-0)/80 transition-opacity hover:opacity-100 underline"
					>
						Apply for a wallet
					</a>
				</p>
			</form>
		</>
	);
};

/* ══════════════════════════════════════════════════════════════
   LAYOUT A — "Floating Card"
   Full-bleed dark gradient · hero left · glass card center
   ══════════════════════════════════════════════════════════════ */
const LayoutA = ({ formContent }: { formContent: React.ReactNode }) => (
	<div
		className="login-page relative flex min-h-screen flex-col items-center justify-center overflow-hidden"
		style={{ background: MG_DARK }}
	>
		<Particles color="255,255,255" opacity={0.05} count={30} />
		<Rings
			ringColor="rgba(255,255,255,0.06)"
			className="top-[8%] -right-[8%]"
		/>
		<Rings
			ringColor="rgba(255,255,255,0.04)"
			className="-bottom-[10%] -left-[15%]"
		/>

		{/* Top bar */}
		<div className="login-topbar absolute top-0 right-0 left-0 z-10 flex items-center justify-between px-5 py-5 lg:px-10 lg:py-7">
			<img
				src="/logo.svg"
				alt="MoneyGram"
				className="h-7 w-auto brightness-0 invert lg:h-9"
			/>
			<span className="flex items-center gap-2 font-semibold text-[10px] text-white/60 uppercase tracking-widest lg:text-[11px]">
				<span
					className="h-1.5 w-1.5 rounded-full bg-(--color-primary)"
					style={{ boxShadow: "0 0 8px var(--color-primary-glow)" }}
				/>
				Partner Integration Portal
			</span>
		</div>

		{/* Center: hero + card */}
		<div className="relative z-10 flex w-full max-w-[1100px] flex-col items-center gap-10 px-5 pt-20 lg:flex-row lg:gap-20 lg:px-12 lg:pt-0">
			{/* Hero — hidden on mobile, visible on lg+ */}
			<div className="login-hero hidden flex-1 lg:block">
				<h1 className="mb-4 font-black text-[3rem] text-white uppercase leading-[1.06] tracking-[-2px]">
					Build the future
					<br />
					of money
					<br />
					<span className="text-(--color-primary)">movement.</span>
				</h1>
				<p className="mb-6 max-w-[340px] text-[15px] text-white/70 leading-relaxed">
					Manage your integration, track onboarding progress, and keep
					compliance in sync.
				</p>
				<div className="flex flex-wrap gap-2">
					{["Partner Onboarding", "Wallet Management", "Compliance & KYC"].map(
						(t) => (
							<span
								key={t}
								className="rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 font-medium text-[11px] text-white/80"
							>
								{t}
							</span>
						),
					)}
				</div>
			</div>

			{/* Glass card — full-width on mobile, fixed on lg+ */}
			<div
				className="login-form-dark w-full max-w-[420px] shrink-0 rounded-xl border border-white/10 p-6 sm:p-9 lg:w-[380px]"
				style={{
					background: "rgba(255,255,255,0.09)",
					backdropFilter: "blur(24px)",
				}}
			>
				{formContent}
			</div>
		</div>

		{/* Secured footer */}
		<div className="login-footer absolute bottom-4 left-1/2 z-10 -translate-x-1/2 text-center text-[10px] text-white/70 lg:bottom-6 lg:whitespace-nowrap lg:text-[11px]">
			Secured by MoneyGram · Partner Integration Portal
		</div>
	</div>
);

/* ══════════════════════════════════════════════════════════════
   LOGIN — main component
   ══════════════════════════════════════════════════════════════ */
const Login: React.FC = () => {
	const [emailForConfirmReset, setEmailForConfirmReset] = useState("");
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [view, setView] = useState<LoginView>("signin");

	const {
		login,
		newPasswordRequired,
		completeNewPassword,
		forgotPassword,
		confirmForgotPassword,
	} = useAuth();
	const navigate = useNavigate();

	const loginForm = useForm<LoginFormData>({
		resolver: zodResolver(loginSchema),
		defaultValues: { email: "", password: "" },
		mode: "onSubmit",
	});

	const forgotForm = useForm<ForgotPasswordFormData>({
		resolver: zodResolver(forgotPasswordSchema),
		defaultValues: { email: "" },
		mode: "onSubmit",
	});

	const confirmResetForm = useForm<ConfirmResetFormData>({
		resolver: zodResolver(confirmResetSchema),
		defaultValues: { resetCode: "", newPassword: "", confirmPassword: "" },
		mode: "onSubmit",
	});

	const newPasswordForm = useForm<NewPasswordRequiredFormData>({
		resolver: zodResolver(newPasswordRequiredSchema),
		defaultValues: { newPassword: "", confirmPassword: "" },
		mode: "onSubmit",
	});

	// Sync email from signin to forgot when user clicks "Forgot password?"
	const handleSwitchToForgot = () => {
		const signinEmail = loginForm.getValues("email");
		if (signinEmail) {
			forgotForm.setValue("email", signinEmail);
		}
		setView("forgot");
	};

	const onValidSignIn = async (data: LoginFormData) => {
		setError("");
		setLoading(true);
		try {
			await login(data.email, data.password);
			navigate("/");
		} catch (err: unknown) {
			if (err instanceof NewPasswordRequiredError) {
				setError("");
			} else {
				const msg = err instanceof Error ? err.message : "Failed to login";
				const errName =
					err instanceof Error ? (err as Error & { name?: string }).name : "";
				const isAuthFailure =
					errName === "NotAuthorizedException" ||
					msg.includes("Incorrect username or password") ||
					msg.toLowerCase().includes("incorrect username");
				setError(
					isAuthFailure
						? `${msg} If your password was recently reset by an administrator, use the new password they provided.`
						: msg,
				);
			}
		} finally {
			setLoading(false);
		}
	};

	const onValidNewPassword = async (data: NewPasswordRequiredFormData) => {
		setError("");
		setLoading(true);
		try {
			await completeNewPassword(data.newPassword);
			navigate("/");
		} catch (err: unknown) {
			setError(
				err instanceof Error ? err.message : "Failed to set new password",
			);
		} finally {
			setLoading(false);
		}
	};

	const onValidForgot = async (data: ForgotPasswordFormData) => {
		setError("");
		setEmailForConfirmReset(data.email);
		setLoading(true);
		try {
			await forgotPassword(data.email.trim());
			setView("confirmReset");
		} catch (err: unknown) {
			const msg = err instanceof Error ? err.message : String(err);
			const isAdminOnly =
				(err instanceof Error && err.name === "NotAuthorizedException") ||
				msg.includes("Contact administrator") ||
				msg.includes("contact administrator");
			setError(
				isAdminOnly
					? "Self-service password reset is not enabled. Please contact your administrator to reset your password."
					: msg || "Failed to send reset code",
			);
		} finally {
			setLoading(false);
		}
	};

	const onValidConfirmReset = async (data: ConfirmResetFormData) => {
		setError("");
		setLoading(true);
		try {
			await confirmForgotPassword(
				emailForConfirmReset.trim(),
				data.resetCode.trim(),
				data.newPassword,
			);
			setView("signin");
			setError("");
			confirmResetForm.reset();
			notifications.success(
				"Password reset. You can now sign in with your new password.",
			);
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Failed to reset password");
		} finally {
			setLoading(false);
		}
	};

	const formProps: FormContentProps = {
		view,
		setView,
		onSwitchToForgot: handleSwitchToForgot,
		emailForDisplay: emailForConfirmReset,
		error,
		loading,
		newPasswordRequired,
		loginForm,
		forgotForm,
		confirmResetForm,
		newPasswordForm,
		onValidSignIn,
		onValidForgot,
		onValidConfirmReset,
		onValidNewPassword,
	};

	const formContent = <FormContent {...formProps} />;

	return <LayoutA formContent={formContent} />;
};

export default Login;
